import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { RMVendorOrderModal } from './RMVendorOrderModal';
import { TechnicalStaffOrderModal } from './TechnicalStaffOrderModal';

const AddEditOrderModalWrapper = ({
  mode,
  open,
  onClose,
  onAdd,
  onEdit,
  formik,
  rmVendors,
  vendorItems,
  vendorCategories,
}) => {
  const isViewMode = useMemo(() => mode === 'view', [mode]);
  const orderData = useMemo(() => {
    if (!isViewMode || !formik.values) return null;

    const items = formik.values.orderedItems?.map((item, index) => ({
      id: item.item_id || `item_${index}`,
      name: item.item_id
        ? (vendorItems.find(v => v._id === item.item_id)?.description || 'Unknown Item')
        : 'Unknown Item',
      description: vendorItems.find(v => v._id === item.item_id)?.description || '',
      price: parseFloat(item.price) || 0,
      quantity: parseInt(item.requestedQuantity) || 1,
      total: (parseFloat(item.price) || 0) * (parseInt(item.requestedQuantity) || 1),
      jobName: item.jobName || '',
      category: vendorCategories.find(c => c._id === item.category)?.request_type || '',
      sku: vendorItems.find(v => v._id === item.item_id)?.sku || '',
      make: vendorItems.find(v => v._id === item.item_id)?.make || '',
      modelNo: vendorItems.find(v => v._id === item.item_id)?.modelNo || '',
      image: '🔧',
    })) || [];

    return {
      orderId: formik.values.orderId || 'N/A',
      placedBy: formik.values.placedBy || 'Unknown',
      placedDate: formik.values.placedDate || new Date().toLocaleString(),
      location: formik.values.location || 'Unknown Location',
      department: formik.values.department || 'Unknown Department',
      deliverTo: {
        name: formik.values.deliverTo?.name || 'Unknown',
        address: formik.values.deliverTo?.address || '',
        city: formik.values.deliverTo?.city || '',
        country: formik.values.deliverTo?.country || '',
      },
      paymentMethod: formik.values.paymentMethod || 'N/A',
      billingAddress: formik.values.billingAddress || 'N/A',
      comments: formik.values.comments || '',
      status: formik.values.status || 'SUBMITTED',
      deliveryStatus: formik.values.deliveryStatus || 'AWAITING_APPROVAL',
      items,
      totals: {
        items: items.reduce((sum, item) => sum + item.total, 0),
        shipping: parseFloat(formik.values.shipping) || 0,
        discount: parseFloat(formik.values.discount) || 0,
        tax: parseFloat(formik.values.tax) || 0,
        total: items.reduce((sum, item) => sum + item.total, 0) +
          (parseFloat(formik.values.shipping) || 0) -
          (parseFloat(formik.values.discount) || 0) +
          (parseFloat(formik.values.tax) || 0),
      },
    };
  }, [isViewMode, formik.values, rmVendors, vendorItems, vendorCategories]);

  return (
    <>
      {isViewMode ? (
        <RMVendorOrderModal
          isOpen={open}
          onClose={onClose}
          orderData={orderData}
        />
      ) : (
        <TechnicalStaffOrderModal
          mode={mode}
          open={open}
          onClose={onClose}
          onAdd={onAdd}
          onEdit={onEdit}
          formik={formik}
          rmVendors={rmVendors}
        />
      )}
    </>
  );
};

AddEditOrderModalWrapper.propTypes = {
  mode: PropTypes.oneOf(['add', 'edit', 'view']).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
  onEdit: PropTypes.func,
  formik: PropTypes.object.isRequired,
  rmVendors: PropTypes.array,
  vendorItems: PropTypes.array,
  vendorCategories: PropTypes.array,
};

export { AddEditOrderModalWrapper };